/* eslint-disable react-refresh/only-export-components -- provider and its typed hooks form one state boundary. */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { BootstrapResponse } from "@aion/xo-core";
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type User,
} from "firebase/auth";

import { bootstrapXO } from "./lib/api";
import { demoSnapshot } from "./lib/demo";
import { auth, googleProvider } from "./lib/firebase";

export const DEMO_MODE = import.meta.env.VITE_XO_DEMO === "true";

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [loading, setLoading] = useState(!DEMO_MODE);

  useEffect(() => {
    if (DEMO_MODE) return;
    return onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
      setLoading(false);
    });
  }, []);

  return {
    user,
    loading,
    signIn: () => signInWithPopup(auth, googleProvider),
    signOut: () => signOut(auth),
  };
};

interface XOContextValue {
  data: BootstrapResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  setData: React.Dispatch<React.SetStateAction<BootstrapResponse | null>>;
}

const XOContext = createContext<XOContextValue | null>(null);

export const XOProvider = ({ children, enabled }: { children: ReactNode; enabled: boolean }) => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Bogota";
  const [data, setData] = useState<BootstrapResponse | null>(() =>
    DEMO_MODE ? demoSnapshot(timeZone) : null,
  );
  const [loading, setLoading] = useState(!DEMO_MODE && enabled);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (DEMO_MODE) return;
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      setData(await bootstrapXO(timeZone));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "XO could not load.");
    } finally {
      setLoading(false);
    }
  }, [enabled, timeZone]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ data, loading, error, refresh, setData }),
    [data, error, loading, refresh],
  );
  return <XOContext.Provider value={value}>{children}</XOContext.Provider>;
};

export const useXO = (): XOContextValue => {
  const value = useContext(XOContext);
  if (!value) throw new Error("useXO must be used inside XOProvider.");
  return value;
};
